import React, { useState } from 'react';
import DataTable from 'react-data-table-component';
import {
    Avatar, Box, Chip, IconButton, Menu, MenuItem, Stack,
    Tooltip, Typography, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import PasswordIcon from '@mui/icons-material/Password';
import EmailIcon from '@mui/icons-material/Email';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import LoadingComponent from '../../../common/components/LoadingComponent';
import DeleteUser from './DeleteUser';
import ResetPassword from './ResetPassword';
import SendWelcomeEmail from './SendWelcomeEmail';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const BRAND = '#0F2FEB';

const AVATAR_COLORS = [
    '#1565C0', '#6A1B9A', '#00695C', '#2E7D32',
    '#E65100', '#AD1457', '#0277BD', '#283593',
];

function getInitials(firstName, lastName) {
    return `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`.toUpperCase();
}

function getAvatarColor(str) {
    let hash = 0;
    for (let i = 0; i < (str || '').length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const tableStyles = {
    rows: {
        style: {
            minHeight: '3.8rem',
            '&:hover': { backgroundColor: '#F3F6FF !important' },
        },
    },
    headRow: {
        style: {
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            backgroundColor: '#F8F9FA',
            borderBottom: '2px solid #E0E0E0',
        },
    },
    pagination: { style: { backgroundColor: '#fff' } },
};

function RowActions({ row, onEditUser }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const navigate = useNavigate();

    const menuOpen = Boolean(anchorEl);
    const openMenu = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
    const closeMenu = () => setAnchorEl(null);
    const run = (fn) => { closeMenu(); fn(); };

    return (
        <>
            <Tooltip title="Actions" arrow>
                <IconButton size="small" onClick={openMenu} sx={{ color: 'text.secondary' }}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={closeMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{ elevation: 3, sx: { minWidth: 215, borderRadius: 2, mt: 0.5 } }}
            >
                <MenuItem onClick={() => run(() => navigate(`/user-usage-audit/${row.username}`))}>
                    <ListItemIcon><ManageSearchIcon fontSize="small" sx={{ color: BRAND }} /></ListItemIcon>
                    <ListItemText>View Usage Audit</ListItemText>
                </MenuItem>

                <MenuItem onClick={() => run(() => onEditUser(row))}>
                    <ListItemIcon><EditRoundedIcon fontSize="small" sx={{ color: BRAND }} /></ListItemIcon>
                    <ListItemText>Edit User</ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem
                    disabled={row.welcome_email_sent}
                    onClick={() => run(() => setEmailOpen(true))}
                >
                    <ListItemIcon>
                        <EmailIcon fontSize="small"
                            sx={{ color: row.welcome_email_sent ? 'text.disabled' : BRAND }} />
                    </ListItemIcon>
                    <ListItemText>
                        {row.welcome_email_sent ? 'Welcome Email Already Sent' : 'Send Welcome Email'}
                    </ListItemText>
                </MenuItem>

                <MenuItem
                    disabled={!row.can_change_password}
                    onClick={() => run(() => setResetOpen(true))}
                >
                    <ListItemIcon>
                        <PasswordIcon fontSize="small"
                            sx={{ color: row.can_change_password ? BRAND : 'text.disabled' }} />
                    </ListItemIcon>
                    <ListItemText>Reset Password</ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem onClick={() => run(() => setDeleteOpen(true))} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteRoundedIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Delete User</ListItemText>
                </MenuItem>
            </Menu>

            <DeleteUser user={row} isOpen={deleteOpen} setsetIsOpen={setDeleteOpen} />
            <ResetPassword user={row} isOpen={resetOpen} setsetIsOpen={setResetOpen} />
            <SendWelcomeEmail user={row} isOpen={emailOpen} setIsOpen={setEmailOpen} />
        </>
    );
}

function formatDate(val) {
    if (!val) return '—';
    return new Date(val).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
    });
}

// Returns { label, tooltip } — recent logins use relative time, older ones use absolute
function formatLastLogin(val) {
    if (!val) return { label: '—', tooltip: null };
    const m = moment(val);
    const diffDays = moment().diff(m, 'days');
    const absolute = m.format('MMM D, YYYY [at] HH:mm');
    if (diffDays === 0) return { label: 'Today', tooltip: absolute };
    if (diffDays < 30) return { label: m.fromNow(), tooltip: absolute };
    return { label: m.format('MMM D, YYYY'), tooltip: null };
}

export default function UserTable({ filteredUsers, isLoading, onEditUser }) {
    const columns = [
        {
            name: 'USER',
            selector: row => row.last_name,
            sortable: true,
            minWidth: '230px',
            cell: row => (
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.5 }}>
                    <Avatar
                        sx={{
                            width: 36, height: 36, fontSize: '0.8rem', fontWeight: 700,
                            bgcolor: getAvatarColor(`${row.first_name}${row.last_name}`),
                        }}
                    >
                        {getInitials(row.first_name, row.last_name)}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" fontWeight={600} lineHeight={1.3}>
                            {row.last_name}, {row.first_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" lineHeight={1}>
                            {row.adid}
                        </Typography>
                    </Box>
                </Stack>
            ),
        },
        {
            name: 'EMAIL',
            selector: row => row.email,
            sortable: true,
            minWidth: '200px',
            cell: row => (
                <Tooltip title={row.email} placement="top" arrow>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                        {row.email}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            name: 'GROUPS',
            selector: row => row.groups?.join(', '),
            minWidth: '180px',
            cell: row => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, py: 0.5 }}>
                    {row.groups?.length > 0 ? (
                        <>
                            {row.groups.slice(0, 2).map(g => (
                                <Chip
                                    title={g}
                                    key={g} label={g} size="small" variant="outlined"
                                    sx={{

                                            maxWidth: 150,
                                              '& .MuiChip-label': {
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                              },fontSize: '0.68rem', height: 20, color: BRAND, borderColor: BRAND }}
                                />
                            ))}
                            {row.groups.length > 2 && (
                                <Tooltip title={row.groups.slice(2).join(', ')} arrow>
                                    <Chip
                                        label={`+${row.groups.length - 2}`} size="small"
                                        sx={{ fontSize: '0.68rem', height: 20, bgcolor: '#EEF1FD', color: BRAND }}
                                    />
                                </Tooltip>
                            )}
                        </>
                    ) : (
                        <Chip
                            label="No Group" size="small" color="warning" variant="outlined"
                            sx={{ fontSize: '0.68rem', height: 20 }}
                        />
                    )}
                </Box>
            ),
        },
        {
            name: 'DATE ADDED',
            selector: row => row.date_added,
            sortable: true,
            cell: row => (
                <Typography variant="body2" color="text.secondary">
                    {formatDate(row.date_added)}
                </Typography>
            ),
        },
        {
            name: 'LAST LOGIN',
            selector: row => row.last_login,
            sortable: true,
            sortFunction: (a, b) => {
                const da = a.last_login ? new Date(a.last_login) : null;
                const db = b.last_login ? new Date(b.last_login) : null;
                if (da && db) return da - db;
                return da ? -1 : db ? 1 : 0;
            },
            cell: row => {
                const { label, tooltip } = formatLastLogin(row.last_login);
                const text = (
                    <Typography variant="body2" color={row.last_login ? 'text.primary' : 'text.disabled'}>
                        {label}
                    </Typography>
                );
                return tooltip
                    ? <Tooltip title={tooltip} arrow placement="top">{text}</Tooltip>
                    : text;
            },
        },
        {
            name: 'STATUS',
            ignoreRowClick: true,
            cell: row => (
                <Chip
                    label={row.welcome_email_sent ? 'Email Sent' : 'Email Pending'}
                    size="small"
                    color={row.welcome_email_sent ? 'success' : 'default'}
                    variant={row.welcome_email_sent ? 'filled' : 'outlined'}
                    sx={{ fontSize: '0.65rem', height: 20 }}
                />
            ),
        },
        {
            name: '',
            cell: row => <RowActions row={row} onEditUser={onEditUser} />,
            ignoreRowClick: true,
            button: true,
            width: '56px',
        },
    ];

    return (
        <DataTable
            columns={columns}
            data={filteredUsers}
            pagination
            progressPending={isLoading}
            progressComponent={<LoadingComponent />}
            highlightOnHover
            customStyles={tableStyles}
            noDataComponent={
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography color="text.secondary">No users found.</Typography>
                </Box>
            }
        />
    );
}